/**
 * Minimal JWT helpers (no external deps).
 *
 * Attenzione: NON validiamo la firma lato client. Questi helper servono solo
 * a leggere `exp` per programmare il refresh proattivo. La validazione vera
 * avviene lato backend.
 */

/** Decodifica il payload di un JWT. Ritorna null se il token è malformato. */
export function decodeJwtPayload<T = Record<string, unknown>>(
  token: string | null | undefined,
): T | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    // base64url → base64 + padding
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/**
 * Ritorna il timestamp di scadenza del JWT in ms (UNIX epoch), oppure null
 * se il token non ha un claim `exp` valido.
 */
export function getJwtExpiryMs(token: string | null | undefined): number | null {
  const payload = decodeJwtPayload<{ exp?: number }>(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp * 1000;
}

/**
 * Ritorna i ms rimanenti prima della scadenza del token. Negativo se già scaduto.
 * Ritorna null se il token non ha un claim `exp` valido.
 */
export function getJwtTimeToExpiryMs(
  token: string | null | undefined,
): number | null {
  const expMs = getJwtExpiryMs(token);
  if (expMs === null) return null;
  return expMs - Date.now();
}
